import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Waves, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function SetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/set-password", { token, password });
      setDone(true);
      toast.success("Password set — you can now log in");
      setTimeout(() => navigate("/login"), 1500);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="public-site hero-glow flex min-h-screen items-center justify-center px-5">
      <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl shadow-black/30 sm:p-10" data-testid="set-password-card">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand text-white"><Waves className="h-5 w-5" /></span>
          <span className="font-heading text-xl font-bold text-slate-900">Just Laundry</span>
        </div>
        {!token && <p className="mt-6 text-sm text-red-600">This link is missing its token. Please use the link from your email.</p>}
        {token && !done && (
          <>
            <h1 className="mt-6 font-heading text-2xl font-bold text-slate-900">Set your password</h1>
            <p className="mt-1 text-sm text-slate-500">Choose a password to access your laundry dashboard.</p>
            <div className="mt-6 space-y-4">
              <div className="space-y-1.5"><Label>New password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="set-password-input" /></div>
              <div className="space-y-1.5"><Label>Confirm password</Label><Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="set-password-confirm" /></div>
              <Button onClick={submit} disabled={busy} className="h-12 w-full rounded-full bg-brand text-base font-bold hover:bg-brand-hover" data-testid="set-password-btn">{busy ? "Saving…" : "Set password"}</Button>
            </div>
          </>
        )}
        {done && (
          <div className="mt-6 text-center" data-testid="set-password-done">
            <CheckCircle2 className="mx-auto h-10 w-10 text-green-500" />
            <p className="mt-3 font-medium text-slate-900">Password set!</p>
            <Link to="/login" className="mt-2 inline-block text-sm text-brand">Go to login</Link>
          </div>
        )}
      </div>
    </div>
  );
}
